import { Td, Text, Tr } from '@chakra-ui/react'
import { ReactNode } from 'react'

import { ActionButton } from '../ActionButton'

export interface ITableErrorRowProps {
  /** Número de colunas da tabela — o mesmo `colSpan` do `<TableEmptyRow>`. */
  colSpan: number
  /** Substantivo do tipo de registro, minúsculo e **no plural**: `setores`, `vagas`, `chamados`. */
  entity?: string
  /** Concordância pt-BR: ligue para substantivo feminino (`carregar as vagas`). */
  feminine?: boolean
  /** Refaz a busca. Sem ele a linha não oferece saída — o que só faz sentido se a tela tiver outra. */
  onRetry?: () => void
  /** Substitui a frase montada. */
  message?: ReactNode
}

/**
 * Linha de falha de carregamento das tabelas do painel — a irmã do `<TableEmptyRow>`.
 *
 * O corpo de uma listagem é um caminho de **quatro** saídas, não de três: carregando, falhou,
 * vazio, e as linhas. Sem o segundo ramo a tela afirma `Nenhum <registro> encontrado` quando a
 * requisição falhou — é o mesmo defeito que o eixo de carregamento fechou do lado do
 * "carregando", reaberto do lado do "falhou", e agravado pelo mesmo motivo: foi o
 * `TableEmptyRow` que deu àquela linha uma frase específica e confiante.
 *
 * Das 52 listagens do painel, **três** tinham alguma UI de erro, e as três divergiam até na
 * pontuação (`Erro ao carregar objetivos de curso`, `Erro ao carregar categorias.`,
 * `Erro ao carregar dinâmicas de ensino.`). A frase agora é uma só —
 * **`Não foi possível carregar os <entity>`**, sem ponto — e `entity` segue a convenção do
 * `TableEmptyRow`, com a diferença de vir no plural, porque a frase pede o plural.
 *
 * O `catch` da página guarda o erro **além** de chamar o `toast.error`: o toast some em cinco
 * segundos e a linha fica, e é a linha que explica a tabela vazia para quem voltar à aba depois.
 */
export default function TableErrorRow({ colSpan, entity, feminine = false, onRetry, message }: ITableErrorRowProps) {
  const article = entity ? (feminine ? 'as' : 'os') : 'os'
  const noun = entity ?? 'registros'
  const text = message ?? `Não foi possível carregar ${article} ${noun}`

  return (
    <Tr>
      {/* O respiro e a cor moram aqui, como no `TableEmptyRow`: a linha é um bloco de mensagem,
          não uma linha de dados, então não segue o ritmo de 49px das demais. */}
      <Td colSpan={colSpan} textAlign="center" color="red.500" py={10}>
        <Text>{text}</Text>
        {onRetry && (
          <ActionButton action="refresh" label="Tentar novamente" showLabel mr={0} mt={3} onClick={onRetry} />
        )}
      </Td>
    </Tr>
  )
}
